/* ==========================================================================
   Seção: Vitrine de produtos
   Arquivo: assets/section-product-showcase.js
   --------------------------------------------------------------------------
   O deslize é a rolagem NATIVA do navegador (overflow-x + scroll-snap). Não
   há transform calculado em JS: no celular o dedo arrasta com a inércia do
   próprio sistema, que é o mais liso que existe.

   AUTOPLAY EDUCADO. Avança um card por vez, mas só quando:
     - a vitrine está na tela e a aba está visível;
     - o mouse não está em cima e nada dentro dela tem foco;
     - o cliente não tocou/arrastou/rolou nela nos últimos segundos.
   No fim da fila volta para o começo. Desligado para "reduzir movimento".
   ========================================================================== */

(function () {
  'use strict';

  var RESUME_AFTER = 5000; // ms sem interação para o autoplay voltar

  var mqReduce = window.matchMedia('(prefers-reduced-motion: reduce)');

  function now() { return Date.now(); }

  function Showcase(root) {
    this.root = root;
    this.track = root.querySelector('[data-psc-track]');
    this.items = [].slice.call(root.querySelectorAll('[data-psc-item]'));
    this.prevBtn = root.querySelector('[data-psc-prev]');
    this.nextBtn = root.querySelector('[data-psc-next]');
    this.bar = root.querySelector('[data-psc-bar]');

    this.autoplay = root.getAttribute('data-autoplay') === 'true' && !mqReduce.matches;
    this.interval = parseFloat(root.getAttribute('data-interval')) || 3500;

    this.visible = false;
    this.hovering = false;
    this.focused = false;
    this.resumeAt = 0;
    this.timer = null;
    this.ticking = false;
    this.dragging = false;
    this.dragMoved = false;

    if (!this.track || !this.items.length) return;
    this.init();
  }

  Showcase.prototype.init = function () {
    var self = this;

    this.onScroll = function () {
      if (self.ticking) return;
      self.ticking = true;
      window.requestAnimationFrame(function () {
        self.ticking = false;
        self.updateUi();
      });
    };
    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll);

    if (this.prevBtn) this.prevBtn.addEventListener('click', function () { self.touch(); self.step(-1); });
    if (this.nextBtn) this.nextBtn.addEventListener('click', function () { self.touch(); self.step(1); });

    // Qualquer gesto do cliente na vitrine segura o autoplay por um tempo.
    ['pointerdown', 'touchstart', 'wheel', 'keydown'].forEach(function (type) {
      self.track.addEventListener(type, function () { self.touch(); }, { passive: true });
    });

    // Só mouse de verdade: no celular o toque dispara um "mouseenter" de
    // compatibilidade sem nunca disparar o "leave", e o autoplay ficaria
    // parado para sempre depois do primeiro toque.
    this.root.addEventListener('pointerenter', function (e) {
      if (e.pointerType === 'mouse') self.hovering = true;
    });
    this.root.addEventListener('pointerleave', function (e) {
      if (e.pointerType === 'mouse') { self.hovering = false; self.touch(); }
    });

    // Só foco de teclado: um toque em "Adicionar" deixa o botão focado e
    // isso não deve congelar a vitrine.
    this.root.addEventListener('focusin', function (e) {
      var keyboard = true;
      try { keyboard = e.target.matches(':focus-visible'); } catch (err) {}
      if (keyboard) self.focused = true;
    });
    this.root.addEventListener('focusout', function (e) {
      if (!self.root.contains(e.relatedTarget)) self.focused = false;
    });

    this.bindDrag();
    this.bindAdd();

    if ('IntersectionObserver' in window) {
      this.io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          self.visible = entry.isIntersecting;
          if (self.visible) self.startTimer(); else self.stopTimer();
        });
      }, { threshold: 0.35 });
      this.io.observe(this.track);
    } else {
      this.visible = true;
      this.startTimer();
    }

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) self.stopTimer();
      else if (self.visible) self.startTimer();
    });

    this.updateUi();
    window.addEventListener('load', this.onScroll);
  };

  /* ---------- geometria ---------- */

  Showcase.prototype.stepSize = function () {
    var styles = window.getComputedStyle(this.track);
    var gap = parseFloat(styles.columnGap || styles.gap) || 0;
    return this.items[0].getBoundingClientRect().width + gap;
  };

  Showcase.prototype.maxScroll = function () {
    return Math.max(0, this.track.scrollWidth - this.track.clientWidth);
  };

  Showcase.prototype.scrollToX = function (x) {
    this.track.scrollTo({ left: x, behavior: mqReduce.matches ? 'auto' : 'smooth' });
  };

  Showcase.prototype.step = function (dir) {
    var size = this.stepSize();
    var target = Math.round((this.track.scrollLeft + dir * size) / size) * size;
    this.scrollToX(Math.max(0, Math.min(target, this.maxScroll())));
  };

  Showcase.prototype.updateUi = function () {
    var max = this.maxScroll();
    var x = this.track.scrollLeft;

    if (this.prevBtn) this.prevBtn.disabled = x <= 4;
    if (this.nextBtn) this.nextBtn.disabled = x >= max - 4;

    if (this.bar) {
      var total = this.track.scrollWidth || 1;
      var ratio = Math.min(1, this.track.clientWidth / total);
      var p = max > 0 ? x / max : 0;
      this.bar.style.width = (ratio * 100).toFixed(2) + '%';
      // translate em % da própria barra: anda (1 - ratio) da trilha inteira
      this.bar.style.transform = 'translate3d(' + (p * (1 - ratio) / ratio * 100).toFixed(2) + '%,0,0)';
    }
  };

  /* ---------- autoplay ---------- */

  Showcase.prototype.touch = function () {
    this.resumeAt = now() + RESUME_AFTER;
  };

  Showcase.prototype.canAdvance = function () {
    return this.visible && !document.hidden && !this.hovering && !this.focused &&
      !this.dragging && now() >= this.resumeAt;
  };

  Showcase.prototype.startTimer = function () {
    if (!this.autoplay || this.timer) return;
    var self = this;
    this.timer = window.setInterval(function () {
      if (!self.canAdvance()) return;
      var max = self.maxScroll();
      if (max <= 4) return;
      if (self.track.scrollLeft >= max - 4) self.scrollToX(0);
      else self.step(1);
    }, this.interval);
  };

  Showcase.prototype.stopTimer = function () {
    if (this.timer) { window.clearInterval(this.timer); this.timer = null; }
  };

  /* ---------- arrastar com o mouse (no toque é nativo) ---------- */

  Showcase.prototype.bindDrag = function () {
    var self = this;
    var startX = 0;
    var startScroll = 0;

    this.track.addEventListener('pointerdown', function (e) {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      if (e.target.closest('[data-psc-add], .psc__add')) return;
      self.dragging = true;
      self.dragMoved = false;
      startX = e.clientX;
      startScroll = self.track.scrollLeft;
    });

    this.track.addEventListener('pointermove', function (e) {
      if (!self.dragging) return;
      var dx = e.clientX - startX;
      if (!self.dragMoved && Math.abs(dx) > 5) {
        self.dragMoved = true;
        self.root.classList.add('psc--dragging');
        try { self.track.setPointerCapture(e.pointerId); } catch (err) {}
      }
      if (self.dragMoved) {
        e.preventDefault();
        self.track.scrollLeft = startScroll - dx;
      }
    });

    function endDrag(e) {
      if (!self.dragging) return;
      self.dragging = false;
      if (e && e.pointerId != null) {
        try { self.track.releasePointerCapture(e.pointerId); } catch (err) {}
      }
      if (self.dragMoved) {
        self.root.classList.remove('psc--dragging');
        var size = self.stepSize();
        var target = Math.round(self.track.scrollLeft / size) * size;
        self.scrollToX(Math.max(0, Math.min(target, self.maxScroll())));
        self.touch();
      }
    }

    this.track.addEventListener('pointerup', endDrag);
    this.track.addEventListener('pointercancel', endDrag);
    this.track.addEventListener('pointerleave', endDrag);

    // Soltar o arrasto em cima de um card não pode abrir o produto.
    this.track.addEventListener('click', function (e) {
      if (self.dragMoved) {
        e.preventDefault();
        e.stopPropagation();
        self.dragMoved = false;
      }
    }, true);

    this.track.addEventListener('dragstart', function (e) { e.preventDefault(); });
  };

  /* ---------- adicionar ao carrinho ---------- */

  Showcase.prototype.bindAdd = function () {
    var self = this;
    this.track.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-psc-add]');
      if (!btn || btn.classList.contains('is-loading')) return;
      e.preventDefault();
      self.touch();
      self.addToCart(btn);
    });
  };

  Showcase.prototype.setLabel = function (btn, text) {
    var label = btn.querySelector('[data-psc-add-label]');
    if (label) label.textContent = text; else btn.textContent = text;
  };

  Showcase.prototype.addToCart = function (btn) {
    var self = this;
    var routes = window.themeRoutes || {};
    var addUrl = (routes.cart_add_url || '/cart/add') + '.js';

    btn.classList.remove('is-added', 'is-error');
    btn.classList.add('is-loading');
    this.setLabel(btn, 'Adicionando…');

    fetch(addUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(btn.getAttribute('data-variant-id')), quantity: 1 }] })
    })
      .then(function (res) {
        return res.json().then(function (data) { return { ok: res.ok, data: data }; });
      })
      .then(function (r) {
        if (!r.ok || r.data.status) throw r.data;

        btn.classList.remove('is-loading');
        btn.classList.add('is-added');
        self.setLabel(btn, 'Adicionado ✓');

        if (window.GNXCart && window.GNXCart.refresh) {
          window.GNXCart.refresh(true).then(function () { window.GNXCart.open(); });
        } else {
          window.location.href = routes.cart_url || '/cart';
        }

        window.setTimeout(function () {
          btn.classList.remove('is-added');
          self.setLabel(btn, 'Adicionar');
        }, 2200);
      })
      .catch(function (err) {
        console.error('Erro ao adicionar ao carrinho', err);
        btn.classList.remove('is-loading');
        btn.classList.add('is-error');
        self.setLabel(btn, (err && err.description) ? 'Indisponível' : 'Tente de novo');
        window.setTimeout(function () {
          btn.classList.remove('is-error');
          self.setLabel(btn, 'Adicionar');
        }, 2600);
      });
  };

  Showcase.prototype.destroy = function () {
    this.stopTimer();
    if (this.io) this.io.disconnect();
    window.removeEventListener('resize', this.onScroll);
    window.removeEventListener('load', this.onScroll);
  };

  /* ====================================================================== */

  var instances = [];

  function mount(scope) {
    [].forEach.call((scope || document).querySelectorAll('[data-psc]'), function (root) {
      if (root.dataset.pscReady === 'true') return;
      root.dataset.pscReady = 'true';
      instances.push(new Showcase(root));
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { mount(); });
  } else {
    mount();
  }

  document.addEventListener('shopify:section:load', function (e) { mount(e.target); });
  document.addEventListener('shopify:section:unload', function (e) {
    var root = e.target.querySelector('[data-psc]');
    instances = instances.filter(function (inst) {
      if (inst.root === root) { inst.destroy(); return false; }
      return true;
    });
  });
})();
